import { Autocomplete } from 'src/components/Autocomplete';
import { Button } from 'src/components/Button';
import { type ChangeEvent, useEffect, useState } from 'react';
import {
	CircularProgress,
	FormControl,
	FormLabel,
	Grid,
} from '@material-ui/core';
import { CloudDownload as CloudDownloadIcon } from '@material-ui/icons';
import { TextField } from 'src/components/TextField';
import Table, { type TableAction } from 'src/components/Table';

import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { Dialog } from 'src/components/Dialog';
import {
	type ICourse,
	type IInstitution,
} from 'src/models';
import { useIsMobile } from 'src/helpers';
import { useStyles } from './CertificatesDialog.styles';
import CalendarIcon from '@material-ui/icons/Event';
import MomentUtils from '@date-io/moment';
import moment from 'moment';

import { useDispatch, useSelector } from 'react-redux';
import courseActions from 'src/store/Courses/actions';
import institutionActions from 'src/store/Institutions/actions';
import userActions from 'src/store/Users/actions';

import { CertificateDialog } from './CertificateDialog';

const columns = [
	{
		key: 'user',
		label: 'Nombre',
		// NOTE:
		// ./src/modules/Private/Courses/Course.tsx[22:8]
		render: (value: any, row: any) => value,
	},
];

interface CertificatedDialogProps {
	open: boolean;
	handleOpen: () => void;
}

export function CertificatesDialog({
	handleOpen,
	open,
}: CertificatedDialogProps) {
	const classes = useStyles();
	// NOTE: Ver todas las notas de ./src/modules/private/Admin.tsx
	const dispatch = useDispatch();
	const isMobile = useIsMobile();

	const user = useSelector((state) => state.auth.data);
	const courses = useSelector((state) => state.courses.courses);
	const institutions = useSelector((state) => state.institutions.institutions);
	const isDownloadingCertificates = useSelector(state => state.users.isDownloadingCertificates)

	const {
		data: certificatesData,
		metadata,
		page,
		pageSize,
	} = useSelector((state) => state.users.certificates);

	const [certificate, setCertificate] = useState<{
		open: boolean;
		userCertificate: any
	}>({ open: false, userCertificate: {} });
	const [institutionId, setInstitutionId] = useState(user.institutionId || 0);
	const [institutionName, setInstitutionName] = useState('');
	const [institution, setInstitution] = useState<IInstitution>({});
	const [courseId, setCourseId] = useState(0);
	const [date, setDate] = useState<Date>(new Date());

	function getCertificates(
		institutionId: any,
		courseId: any,
		year: number,
		page?: number,
		pageSize?: number,
	) {
		dispatch(
			userActions.getCertificates(
				institutionId,
				courseId,
				year,
				page,
				pageSize,
			),
		);
	}

	// NOTE:
	// ./src/modules/Private/Courses/Course.tsx[77:8]
	function handleInstitutionChange(event: ChangeEvent<HTMLInputElement>) {
		setInstitutionId(parseInt(event.target.value));
		dispatch(courseActions.getByInstitution(parseInt(event.target.value)));
	}

	const actions: TableAction[] = [
		{
			// NOTE:
			// ./src/modules/Private/Courses/Course.tsx[22:8]
			disabled: (row: any) => !row.passed,

			label: 'Descargar constancia',
			// NOTE:
			// ./src/modules/Private/Courses/Course.tsx[22:8]
			onClick: (row: any) =>
				dispatch(
					courseActions.downloadUserCertificate(
						row.userId,
						row.courseId,
						row.user,
						institutionName,
						row.courseName,
					),
				),
		},
	];

	// NOTE:
	// ./src/modules/Private/Courses/Course.tsx[77:8]
	async function handleBulkdownload() {
		dispatch(userActions.downloadCertificates({
			institutionId,
			courseId,
			year: moment(date).year(),
			institutionName,
		}))
	}

	useEffect(() => {
		setInstitutionId(user?.institutionId || 0);
	}, [user?.institutionId]);

	useEffect(() => {
		setInstitutionName(institution.name || '');
	}, [institution.id]);

	useEffect(() => {
		if (open) {
			if (user.type === 'Admin') dispatch(institutionActions.getAll());
			else dispatch(courseActions.getByInstitution(user.institutionId));
		} else {
			setInstitutionId(0);
			setInstitution({});
			setCourseId(0);
			dispatch(
				userActions.setCertificates([], {
					totalPages: 0,
					totalResults: 0,
				}),
			);
		}
	}, [open]);

	return (
		<>
			<CertificateDialog
				certificate={certificate.userCertificate}
				handleOpen={() => {
					setCertificate({ open: false, userCertificate: {} });
				}}
				institutionName={institutionName}
				open={certificate.open}
			/>

			<Dialog
				content={
					<Grid container spacing={2}>
						<Grid item md={6} xs={12}>
							<Autocomplete
								getOptionLabel={(option: any) => option.name}
								label={'Institución'}
								value={institution}
								// NOTE:
								// ./src/modules/Private/Courses/Course.tsx[22:8]
								onChange={(
									e: ChangeEvent<any>,
									values: any,
								) => {
									if (values) {
										const event = {
											target: { value: values.id },
										}
										// @ts-ignore
										handleInstitutionChange(event);
										setInstitution(
											institutions.find(
												(institution: IInstitution) =>
													institution.id ==
													values.id,
											),
										);
									} else {
										setInstitutionId(0);
										setInstitution({});
									}

									setCourseId(0);
								}}
								// NOTE:
								// ./src/modules/Private/Courses/Course.tsx[22:8]
								options={institutions}
							/>
						</Grid>

						<Grid item md={6} xs={12}>
							<Autocomplete
								getOptionLabel={(option: any) => option.name}
								label={'Curso'}
								value={courses.find(
									(course: ICourse) => course.id === courseId,
								)}
								// NOTE:
								// ./src/modules/Private/Courses/Course.tsx[22:8]
								onChange={(
									e: ChangeEvent<any>,
									values: any,
								) => {
									if (values) {
										setCourseId(values.id);
										getCertificates(
											institutionId,
											values.id,
											moment(date).year(),
											1,
											pageSize,
										);
									} else setCourseId(0);
									dispatch(
										userActions.setCertificates([], {
											totalPages: 0,
											totalResults: 0,
										}),
									);
								}}
								// NOTE:
								// ./src/modules/Private/Courses/Course.tsx[22:8]
								options={courses}
							/>
						</Grid>

						<Grid item md={6} xs={12}>
							<TextField
								helperText={
									'Solo se modificará para esta impresión'
								}
								label={'Nombre de institución en constancia'}
								multiline
								onChange={(
									event: ChangeEvent<HTMLInputElement>,
								) => {
									setInstitutionName(event.target.value);
								}}
								value={institutionName}
							/>
						</Grid>

						<Grid item md={6} xs={12}>
							<FormControl fullWidth>
								<FormLabel> Año </FormLabel>

								<MuiPickersUtilsProvider
									locale={'es'}
									utils={MomentUtils}
								>
									<DatePicker
										InputProps={{
											endAdornment: <CalendarIcon />,
										}}
										autoOk
										format={'YYYY'}
										value={date}
										// NOTE:
										// ./src/modules/Private/Courses/Course.tsx[22:8]
										inputVariant={'outlined'}
										onChange={(date: any) => {
											setDate(date);

											if (institutionId && courseId) {
												getCertificates(
													institutionId,
													courseId,
													moment(date).year(),
													1,
													pageSize,
												);
											}
										}}
										views={['year']}
									/>
								</MuiPickersUtilsProvider>
							</FormControl>
						</Grid>

						<Grid
							className={classes.downloadButtonContainer}
							item
							xs={12}
						>
							<Button
								backgroundColor={'white'}
								disabled={metadata.totalResults == 0 || isDownloadingCertificates}
								onClick={handleBulkdownload}
								startIcon={<CloudDownloadIcon />}
							>
								{isDownloadingCertificates ? (
									<CircularProgress size={26} />
								) : (
									'Descargar todo'
								)}
							</Button>
						</Grid>

						<Grid item xs={12}>
							<Table
								actions={actions}
								columns={columns}
								data={certificatesData}
								handlePagination={(
									event: any,
									newPage: number,
								) => {
									getCertificates(
										institution.id!,
										courseId,
										moment(date).year(),
										newPage,
										pageSize,
									);
									dispatch(
										userActions.getCertificates(
											institution.id!,
											courseId,
											moment(date).year(),
											newPage,
											pageSize,
										),
									);
								}}
								metadata={metadata}
								page={page}
							/>
						</Grid>
					</Grid>
				}
				fullScreen={isMobile}
				handleOpen={handleOpen}
				maxWidth={'lg'}
				open={open}
				title={'Constancias'}
			/>
		</>
	);
}
